/**
 * Locked preview of Team Admin → Identity Providers for plans without SSO.
 *
 * When `GET /auth/v1/identity-providers` answers 402 the page must not collapse
 * to a lone banner (or, worse, a blank workspace once a modal is dismissed).
 * Instead it keeps the whole screen in one scrollable view:
 *
 *   1. the upgrade CTA (dismissible — the upgrade path stays in the table);
 *   2. the service-provider endpoints a school pastes into its IdP, for
 *      reference, derived from this deployment's origin and the tenant id the
 *      same way the auth service builds them (`services/auth/src/routes/saml.ts`);
 *   3. the provider table in a disabled, "no providers yet" state whose empty
 *      action is the Upgrade button.
 *
 * The request is made with `handlesTierLimit`, so the app-wide
 * `TierLimitBoundary` modal never opens over this inline notice.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession } from '../../app/session-context';
import { useTenantSlug } from '../../app/tenant-slug';
import { EmptyState } from '../../components/empty-state';
import { PageHeader } from '../../components/page-header';
import { TierLimitCta, formatPlanName, readTierLimitDetail } from '../../components/tier-limit-cta';
import type { ApiError } from '../../lib/api-error';
import { APP_VIEWS } from '../../lib/constants';

import { ServiceProviderDetails } from './federation-endpoints';

/** Same shapes the auth service publishes in the list envelope's `meta`. */
export function previewEndpoints(origin: string, tenantId: string | null) {
  const tenant = tenantId ?? '{tenant-id}';
  return {
    saml_acs_url: `${origin}/auth/v1/saml/acs`,
    saml_entity_id: `${origin}/auth/v1/saml/metadata/${tenant}`,
    saml_metadata_url: `${origin}/auth/v1/saml/metadata?tenant_id=${tenant}`,
    scim_base_url: `${origin}/scim/v2`,
  };
}

const LOCKED_COLUMNS = ['Provider', 'Protocol', 'Status', 'Allowed personas', 'Last used'];

export interface LockedIdentityProvidersProps {
  tierError: ApiError;
}

export function LockedIdentityProviders({ tierError }: LockedIdentityProvidersProps): JSX.Element {
  const navigate = useNavigate();
  const tenantSlug = useTenantSlug();
  const { session } = useSession();
  const [isCtaDismissed, setIsCtaDismissed] = useState(false);
  const detail = readTierLimitDetail(tierError);
  const minimumPlan = detail?.minimumPlan ?? 'elite';
  const planName = formatPlanName(minimumPlan) ?? 'Elite';
  const origin = globalThis.location?.origin ?? '';
  const endpoints = previewEndpoints(origin, session?.tenantId ?? null);

  const upgrade = (): void => {
    const limit = encodeURIComponent(detail?.limitKey ?? 'saml_scim');
    navigate(`/app/${tenantSlug ?? ''}/${APP_VIEWS.billing}?tab=plan&limit=${limit}`);
  };

  return (
    <section className="stack federation federation--locked" data-testid="federation-locked">
      <PageHeader
        title="Identity Providers"
        context={`SAML 2.0 single sign-on and SCIM 2.0 directory sync — available on the ${planName} plan.`}
        actions={
          <button type="button" className="btn btn--primary" disabled>
            Add Provider
          </button>
        }
      />

      {isCtaDismissed ? null : (
        <TierLimitCta
          limit={detail?.limitKey ?? 'saml_scim'}
          featureDescription="SAML 2.0 single sign-on and SCIM 2.0 directory sync"
          message={tierError.message}
          currentPlan={detail?.currentPlan ?? null}
          minimumPlan={minimumPlan}
          onUpgrade={upgrade}
          onDismiss={() => setIsCtaDismissed(true)}
        />
      )}

      <ServiceProviderDetails
        endpoints={endpoints}
        description="For reference: once SSO is on, these are the values you paste into your identity provider. They are the same for every provider you add."
      />

      <section className="data-table federation__locked-table" aria-disabled="true">
        <div className="data-table__scroll">
          <table className="data-table__table">
            <caption className="visually-hidden">
              Configured identity providers (locked on this plan)
            </caption>
            <thead>
              <tr>
                {LOCKED_COLUMNS.map((column) => (
                  <th key={column} scope="col" data-align="start">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody />
          </table>
        </div>
        <div className="data-table__empty" role="status" data-testid="federation-locked-empty">
          <EmptyState
            title="No identity providers yet"
            description={`Upgrade to ${planName} to connect your school's SSO so staff sign in with their district account, or let your directory provision users automatically.`}
            action={
              <button type="button" className="btn btn--primary" onClick={upgrade}>
                Upgrade to {planName}
              </button>
            }
            isCompact
          />
        </div>
      </section>
    </section>
  );
}
